/**
 * Funnel Templates Hooks - Data fetching and mutations for templates
 * 
 * Uses TanStack Query for:
 * - List templates (system + tenant)
 * - Get template details
 * - Create template from funnel
 * - Delete template
 * - Create funnel from template
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { funnelKeys } from "./useFunnels";

// Query Keys
export const templateKeys = {
  all: ["funnel-templates"],
  list: (category) => [...templateKeys.all, "list", category],
  detail: (id) => [...templateKeys.all, "detail", id],
};

/**
 * Fetch all templates (system + tenant)
 */
export function useFunnelTemplates(category = null) {
  return useQuery({
    queryKey: templateKeys.list(category),
    queryFn: async () => {
      const params = category ? { category } : {};
      const { data } = await api.get("/funnel-templates", { params });
      return data;
    },
  });
}

/**
 * Fetch single template with full graph for preview
 */
export function useFunnelTemplate(id) {
  return useQuery({
    queryKey: templateKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get(`/funnel-templates/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

/**
 * Create template from existing funnel (Save as Template)
 */
export function useSaveAsTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ sourceFunnelId, name, description, category }) => {
      const { data } = await api.post("/funnel-templates", 
        { name, description, category },
        { params: { source_funnel_id: sourceFunnelId } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
      toast({ title: "Template created successfully" });
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to create template";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Delete a tenant template
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/funnel-templates/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
      toast({ title: "Template deleted" });
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to delete template";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Create funnel from template
 */
export function useCreateFunnelFromTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ templateId, name, slug }) => {
      const { data } = await api.post(
        `/funnels/from-template/${templateId}`,
        null,
        { params: { name, slug } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: funnelKeys.list() });
      toast({ title: "Funnel created from template" });
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to create funnel";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}
